import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserCheck, Search, CheckCircle2, Clock, XCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { getVisitorByCode } from '../../services/api';

const VisitorPortal = () => {
  const [gatePassCode, setGatePassCode] = useState('');
  const [visitor, setVisitor] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await getVisitorByCode(gatePassCode);
      setVisitor(data);
      toast.success('Visitor details found!');
    } catch (error) {
      toast.error('Gate pass code not found');
      setVisitor(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: Clock, variant: 'secondary', label: 'Pending Approval' },
      approved: { icon: CheckCircle2, variant: 'default', label: 'Approved' },
      checked_in: { icon: UserCheck, variant: 'default', label: 'Checked In' },
      checked_out: { icon: CheckCircle2, variant: 'outline', label: 'Checked Out' },
      rejected: { icon: XCircle, variant: 'destructive', label: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="px-3 py-1">
        <Icon className="h-4 w-4 mr-1" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="container mx-auto max-w-4xl">
        {/* Header with Home Button */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => window.location.href = '/'}
            className="flex items-center gap-2 text-slate-600 hover:text-primary transition-colors"
            data-testid="visitor-home-btn"
          >
            <Lock className="h-5 w-5" />
            <span className="font-semibold">FortiHome</span>
          </button>
          <button
            onClick={() => window.location.href = '/login'}
            className="text-sm text-slate-600 hover:text-secondary transition-colors font-medium"
          >
            Login / Register
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-16 w-16 bg-secondary rounded-2xl flex items-center justify-center">
              <UserCheck className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Visitor Portal</h1>
          <p className="text-base md:text-lg text-slate-600">Check your gate pass status instantly</p>
        </div>

        {/* Instructions Card */}
        <Card className="shadow-xl border-slate-200 mb-6">
          <CardHeader className="bg-blue-50 border-b border-blue-100">
            <CardTitle className="text-lg flex items-center gap-2 text-blue-900">
              <Search className="h-5 w-5" />
              How to Use This Portal
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6">
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="h-8 w-8 bg-secondary text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">1</div>
                <div>
                  <p className="font-semibold text-slate-900">Get Your Gate Pass Code</p>
                  <p className="text-sm text-slate-600">Your host (the resident you're visiting) will register you in the system and share an 8-character gate pass code with you via phone or message.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="h-8 w-8 bg-secondary text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">2</div>
                <div>
                  <p className="font-semibold text-slate-900">Enter the Code Below</p>
                  <p className="text-sm text-slate-600">Type or paste your 8-character gate pass code in the search box below and click Search.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="h-8 w-8 bg-secondary text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">3</div>
                <div>
                  <p className="font-semibold text-slate-900">View Your Status</p>
                  <p className="text-sm text-slate-600">You'll see your visitor details, host information, and current approval status. Show this to security at the gate.</p>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-900"><strong>Note:</strong> If you don't have a gate pass code, please ask your host to register you in the FortiHome system first.</p>
            </div>
          </CardContent>
        </Card>

        {/* Search Card */}
        <Card className="shadow-xl border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Search className="h-5 w-5\" />
              Search Your Gate Pass
            </CardTitle>
            <CardDescription>Enter your 8-character gate pass code</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="gatepass" className="text-base">Gate Pass Code</Label>
                <Input
                  id="gatepass"
                  placeholder="Enter 8-character code (e.g., ABC12345)"
                  value={gatePassCode}
                  onChange={(e) => setGatePassCode(e.target.value.toUpperCase())}
                  required
                  data-testid="visitor-gatepass-input"
                  className="h-14 text-lg font-mono text-center"
                  maxLength={8}
                />
                <p className="text-xs text-slate-500">Example format: 79EF8FC9 or 2FA0581F</p>
              </div>
              <Button type="submit" className="w-full h-12 bg-secondary hover:bg-secondary/90 text-lg" disabled={loading} data-testid="visitor-search-btn">
                {loading ? 'Searching...' : 'Search Gate Pass'}
              </Button>
            </form>

            {visitor && (
              <div className="mt-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border-2 border-slate-200" data-testid="visitor-details">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-semibold text-slate-900 mb-1">{visitor.name}</h3>
                    <p className="text-slate-600">{visitor.phone}</p>
                  </div>
                  {getStatusBadge(visitor.status)}
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Host Name</p>
                      <p className="font-medium text-slate-900">{visitor.host_name}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Unit Number</p>
                      <p className="font-medium text-slate-900">{visitor.host_unit}</p>
                    </div>
                  </div>

                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Purpose of Visit</p>
                    <p className="font-medium text-slate-900">{visitor.purpose}</p>
                  </div>

                  <div className="p-4 bg-secondary/10 rounded-lg border-2 border-secondary">
                    <p className="text-sm text-slate-600 mb-1">Your Gate Pass Code</p>
                    <p className="text-3xl font-bold text-secondary font-mono tracking-wider\">{visitor.gate_pass_code}</p>
                    <p className="text-xs text-slate-500 mt-1">Show this code to security at the gate</p>
                  </div>

                  {visitor.entry_time && (
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Entry Time</p>
                      <p className="font-medium text-slate-900">{new Date(visitor.entry_time).toLocaleString()}</p>
                    </div>
                  )}

                  {visitor.exit_time && (
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Exit Time</p>
                      <p className="font-medium text-slate-900">{new Date(visitor.exit_time).toLocaleString()}</p>
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-900">
                      {visitor.status === 'approved' && '✓ You are approved! Proceed to the security gate with this code.'}
                      {visitor.status === 'pending' && '⏳ Your visit is pending approval from security.'}
                      {visitor.status === 'checked_in' && '✓ You are currently checked in. Enjoy your visit!'}
                      {visitor.status === 'checked_out' && '✓ Your visit is complete. Thank you!'}
                      {visitor.status === 'rejected' && '✗ Your visit request was not approved. Please contact your host.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Section */}
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-600">
            Need help? Contact your host or the society's security desk.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VisitorPortal;